import LoadingState from '../../../ReusableComponents/LoadingState';
import ResponsiveTable from '../../../ReusableComponents/ResponsiveTable';
import React, { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { CircularProgress } from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
import { MdDownload, MdRefresh } from "react-icons/md";
import { useDispatch } from "react-redux";
import Request from "../../../../Request";
import { logout } from "../../../../actions/AppActions";
import Search from "../../../ReusableComponents/Search";
import "./DashboardReports.css";

const jobStatusText = status => {
    if (status === "0") return "Pendente";
    if (status === "1") return "Executando";
    if (status === "2") return "Concluído";
    if (status === "3") return "Erro";
    return "-";
};

const loadLeaflet = () => new Promise((resolve, reject) => {
    if (window.L && window.L.heatLayer) {
        resolve(window.L);
        return;
    }

    if (!document.getElementById("leaflet-css")) {
        const link = document.createElement("link");
        link.id = "leaflet-css";
        link.rel = "stylesheet";
        link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
        document.head.appendChild(link);
    }

    const loadScript = (id, src) => new Promise((scriptResolve, scriptReject) => {
        const existing = document.getElementById(id);
        if (existing) {
            existing.addEventListener("load", scriptResolve, { once: true });
            if ((id === "leaflet-js" && window.L) || (id === "leaflet-heat-js" && window.L && window.L.heatLayer)) scriptResolve();
            return;
        }
        const script = document.createElement("script");
        script.id = id;
        script.src = src;
        script.async = true;
        script.onload = scriptResolve;
        script.onerror = scriptReject;
        document.body.appendChild(script);
    });

    loadScript("leaflet-js", "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js")
        .then(() => loadScript("leaflet-heat-js", "https://unpkg.com/leaflet.heat@0.2.0/dist/leaflet-heat.js"))
        .then(() => resolve(window.L))
        .catch(reject);
});

const GeoIpDashboard = ({ faculdade_id }) => {
    const dispatch = useDispatch();
    const mapContainerRef = useRef(null);
    const leafletMapRef = useRef(null);
    const leafletLayersRef = useRef([]);
    const mapLoadedRef = useRef(false);
    const mapBoundsFittedRef = useRef(false);
    const [locations, setLocations] = useState([]);
    const [mapPoints, setMapPoints] = useState([]);
    const [job, setJob] = useState(null);
    const [totals, setTotals] = useState({ total_locations: 0, total_logs: 0 });
    const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [mapError, setMapError] = useState("");
    const [message, setMessage] = useState("");

    const getLocations = async (page = 1, searchValue = search) => {
        if (!faculdade_id) return;
        setLoading(true);
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("per_page", 20);
        params.append("include_map_points", !mapLoadedRef.current || (job != null && (job.status === "0" || job.status === "1")) ? "1" : "0");
        if (searchValue) params.append("search", searchValue);

        const response = await Request.Get(`faculdade/get_ip_locations_faculdade/${faculdade_id}?${params.toString()}`);
        setLoading(false);
        if (response === undefined) return;
        if (response.message === "Unauthenticated.") {
            dispatch(logout());
            return;
        }
        if (response.errors) {
            setMessage(response.errors.error || "Não foi possível carregar as localizações.");
            return;
        }
        setLocations(response.locations || []);
        if (response.map_points) {
            setMapPoints(response.map_points);
            mapLoadedRef.current = true;
        }
        setJob(response.job || null);
        setTotals({
            total_locations: response.total_locations || 0,
            total_logs: response.total_logs || 0,
        });
        setPagination(response.pagination || { current_page: 1, last_page: 1, total: 0 });
    };

    const generateLocations = async () => {
        setGenerating(true);
        const response = await Request.Post(`faculdade/generate_ip_locations_faculdade/${faculdade_id}`, new FormData());
        setGenerating(false);
        if (response === undefined) return;
        if (response.message === "Unauthenticated.") {
            dispatch(logout());
            return;
        }
        if (response.errors) {
            setMessage(response.errors.error || "Não foi possível gerar as localizações.");
            return;
        }
        if (response.message) setMessage(response.message);
        setJob(response.job || null);
        mapLoadedRef.current = false;
        getLocations();
    };

    const downloadLocations = async () => {
        setDownloading(true);
        const response = await Request.Get(`faculdade/download_ip_locations_faculdade/${faculdade_id}`);
        setDownloading(false);
        if (response === undefined) return;
        if (response.message === "Unauthenticated.") {
            dispatch(logout());
            return;
        }
        if (response.errors) {
            setMessage(response.errors.error || "Não foi possível baixar a planilha.");
            return;
        }
        if (response.path) {
            window.open(response.path, "_blank");
            setMessage("Planilha gerada. O download foi aberto em uma nova aba.");
        } else if (response.message) {
            setMessage(response.message);
        }
    };

    const refreshLocations = () => {
        mapLoadedRef.current = false;
        getLocations(pagination.current_page || 1, search);
    };

    useEffect(() => {
        mapLoadedRef.current = false;
        mapBoundsFittedRef.current = false;
        setMapPoints([]);
        getLocations(1, "");
        return () => {
            if (leafletMapRef.current != null) {
                leafletMapRef.current.remove();
                leafletMapRef.current = null;
                leafletLayersRef.current = [];
            }
        };
    }, [faculdade_id]);

    useEffect(() => {
        if (job == null || (job.status !== "0" && job.status !== "1")) return;
        const interval = setInterval(() => getLocations(pagination.current_page || 1, search), 30000);
        return () => clearInterval(interval);
    }, [job, pagination.current_page, search]);

    useEffect(() => {
        const timeout = setTimeout(() => getLocations(1, search), 450);
        return () => clearTimeout(timeout);
    }, [search]);

    useEffect(() => {
        if (mapContainerRef.current == null) return;

        loadLeaflet()
            .then(L => {
                setMapError("");
                if (leafletMapRef.current == null) {
                    leafletMapRef.current = L.map(mapContainerRef.current, {
                        scrollWheelZoom: true,
                        worldCopyJump: true,
                    }).setView([-14.235, -51.9253], 4);

                    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
                        attribution: "&copy; OpenStreetMap",
                        maxZoom: 18,
                    }).addTo(leafletMapRef.current);
                }

                leafletLayersRef.current.forEach(layer => leafletMapRef.current.removeLayer(layer));
                leafletLayersRef.current = [];

                const bounds = [];
                const heatPoints = [];

                mapPoints.forEach(item => {
                    const lat = parseFloat(item.latitude);
                    const lng = parseFloat(item.longitude);
                    if (Number.isNaN(lat) || Number.isNaN(lng)) return;

                    bounds.push([lat, lng]);
                    heatPoints.push([lat, lng, Math.max(0.75, Number(item.candidate_count) || 1)]);
                    const popup = document.createElement("div");
                    const label = document.createElement("strong");
                    label.textContent = `${Number(item.candidate_count) || 1} candidato(s)`;
                    popup.appendChild(label);
                    popup.appendChild(document.createElement("br"));
                    popup.appendChild(document.createTextNode([item.city, item.region, item.country].filter(Boolean).join(", ") || "-"));
                    const marker = L.circleMarker([lat, lng], {
                        radius: Math.min(16, 6 + Math.log2(Math.max(1, Number(item.candidate_count) || 1))),
                        color: "#0f766e",
                        fillColor: "#14b8a6",
                        fillOpacity: 0.85,
                        weight: 2,
                    }).bindPopup(popup);
                    marker.addTo(leafletMapRef.current);
                    leafletLayersRef.current.push(marker);
                });

                if (heatPoints.length > 0 && L.heatLayer) {
                    const heat = L.heatLayer(heatPoints, {
                        radius: 32,
                        blur: 24,
                        maxZoom: 8,
                    });
                    heat.addTo(leafletMapRef.current);
                    leafletLayersRef.current.push(heat);
                }

                setTimeout(() => {
                    if (leafletMapRef.current == null) return;
                    leafletMapRef.current.invalidateSize();
                    if (bounds.length > 0 && !mapBoundsFittedRef.current) {
                        leafletMapRef.current.fitBounds(bounds, { padding: [36, 36], maxZoom: 9 });
                        mapBoundsFittedRef.current = true;
                    }
                }, 150);
            })
            .catch(() => setMapError("Não foi possível carregar o mapa. Verifique a conexão e tente novamente."));
    }, [mapPoints]);

    return (
        <div className="dashboard-report-card">
            <div className="dashboard-report-header">
                <div>
                    <h5>Mapa e geolocalização por IP</h5>
                    <p>Mapa baseado no primeiro IP registrado na tela de aceite de regras de cada candidato. As localizações já geradas são reaproveitadas.</p>
                    {job != null && <span className="dashboard-report-job">
                        Status: <b>{jobStatusText(job.status)}</b>
                        {job.total_progresso > 0 && <> ({job.progresso || 0} de {job.total_progresso})</>}
                    </span>}
                    {message && <span className="dashboard-report-job">{message}</span>}
                </div>
                <div className="dashboard-report-actions">
                    <Button variant="light" onClick={refreshLocations} disabled={loading}>
                        {loading ? <CircularProgress size={16} style={{ marginRight: 8 }} /> : <MdRefresh style={{ marginRight: 8 }} />}
                        Atualizar
                    </Button>
                    <Button variant="warning" onClick={generateLocations} disabled={generating || (job != null && (job.status === "0" || job.status === "1"))}>
                        {generating ? <CircularProgress size={16} style={{ color: "white", marginRight: 8 }} /> : <MdRefresh style={{ marginRight: 8 }} />}
                        Gerar localizações
                    </Button>
                    <Button variant="success" onClick={downloadLocations} disabled={downloading}>
                        {downloading ? <CircularProgress size={16} style={{ color: "white", marginRight: 8 }} /> : <MdDownload style={{ marginRight: 8 }} />}
                        Baixar planilha
                    </Button>
                </div>
            </div>

            <div className="dashboard-report-summary">
                <div>
                    <span>{totals.total_locations}</span>
                    <p>localizações no mapa</p>
                </div>
                <div>
                    <span>{totals.total_logs}</span>
                    <p>candidatos que aceitaram as regras</p>
                </div>
            </div>

            <div className="dashboard-report-filter">
                <Search
                    hide_class
                    className="dashboard-report-search"
                    placeholder="Nome, CPF ou inscrição"
                    ValueChange={value => setSearch(value)}
                />
            </div>

            <div className="dashboard-map-wrapper">
                {loading && <div className="dashboard-map-loading"><LoadingState title="Carregando mapa" /></div>}
                {mapError ? <div className="dashboard-map-error">{mapError}</div> : <div ref={mapContainerRef} className="dashboard-map" />}
            </div>

            <div className="table-responsive dashboard-report-table">
                <ResponsiveTable className="table table-striped">
                    <thead>
                        <tr>
                            <th>Candidato</th>
                            <th>Seletivo</th>
                            <th>IP</th>
                            <th>Local</th>
                        </tr>
                    </thead>
                    <tbody>
                        {locations.map(item => (
                            <tr key={item.id}>
                                <td>
                                    <b>{item.candidate_name}</b>
                                    <small>{item.candidate_email}</small>
                                </td>
                                <td><span className="dashboard-report-badge neutral">{item.processo_name || "-"}</span></td>
                                <td><span className="dashboard-report-badge info">{item.ip || "-"}</span></td>
                                <td><span className="dashboard-report-badge success">{[item.city, item.region, item.country].filter(Boolean).join(", ") || "-"}</span></td>
                            </tr>
                        ))}
                    </tbody>
                </ResponsiveTable>
                {loading && <LoadingState title="Carregando relatório de IP" description="Consultando os dados de acesso e localização." />}
                {!loading && locations.length === 0 && <div className="dashboard-report-empty">Nenhuma localização gerada ainda.</div>}
            </div>

            <Pagination
                className={(pagination.last_page === 1 ? "hide" : "") + " hidethis"}
                style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}
                count={pagination.last_page || 1}
                value={pagination.current_page || 1}
                onChange={(_, value) => getLocations(value, search)}
            />
        </div>
    );
};

export default GeoIpDashboard;
